/**
 * Autosave and automatic version snapshots.
 * Saves a couple of seconds after the last edit (and at least every 15 s while editing). The first save of an editing
 * session snapshots the level as it was before (so the session can always be undone), and further snapshots are
 * taken every 10 minutes of editing. The server thins old automatic snapshots.
 */
import type { LevelDoc } from '../model/format.ts';
import { gameData } from '../data/gameData.ts';
import { isAuthError } from '../auth/session.ts';
import { loadLevel, saveLevel, saveLevelLocally } from '../data/remoteStore.ts';
import { createVersion, loadVersion, type VersionMeta } from '../data/versions.ts';
import { isLobbyGuest } from '../lobby/client.ts';
import { uid, useEditor, withoutDerived } from './store.ts';
import { renderThumbnail } from './thumbnail.ts';

export type SaveStatus = 'saved' | 'pending' | 'saving' | 'offline' | 'denied' | 'lobby';

const IDLE_MS = 2000;
const MAX_WAIT_MS = 15_000;
const SNAPSHOT_EVERY_MS = 10 * 60_000;

interface Session {
  id: string;
  opened: LevelDoc;
  /** the pre-edit snapshot was taken */
  started: boolean;
  lastSnapshotAt: number;
  /** last doc that went into a snapshot */
  snapshotDoc: LevelDoc | null;
  /** last doc that reached the server */
  savedDoc: LevelDoc | null;
}

let session: Session | null = null;
let timer: ReturnType<typeof setTimeout> | undefined;
let firstDirtyAt = 0;
let saving: Promise<boolean> | null = null;

const status = (saveStatus: SaveStatus) => useEditor.getState().set({ saveStatus });

async function snapshot(doc: LevelDoc, opts: { label?: string; pinned?: boolean } = {}): Promise<VersionMeta | null> {
  if (isLobbyGuest(doc.id)) return null;
  try {
    const r = await createVersion(doc, { thumb: renderThumbnail(doc), ...opts });
    return r.version;
  } catch {
    return null;
  }
}

/** Before the first save of a session: keep what is on the server now (or the doc as opened) as a version. */
async function snapshotBefore(s: Session) {
  const prev = await loadLevel<LevelDoc>(s.id);
  const builtin = gameData().project.levels.some((l) => l.id === s.id);
  await snapshot(prev ?? s.opened);
  if (!prev && builtin) useEditor.getState().notify('Your changes are saved as your own copy (Levels → Saved levels); the original stays available');
}

function schedule() {
  clearTimeout(timer);
  const wait = Math.max(0, Math.min(IDLE_MS, firstDirtyAt + MAX_WAIT_MS - Date.now()));
  timer = setTimeout(() => void saveNow(), wait);
}

/** Save the open level now (local copy first, then the server). Resolves true when the server has it. */
export async function saveNow(): Promise<boolean> {
  clearTimeout(timer);
  if (saving) await saving;
  const st = useEditor.getState();
  const doc = st.doc;
  if (!doc) return false;
  if (isLobbyGuest(doc.id)) {
    saveLevelLocally(doc);
    status('lobby');
    return false;
  }
  const s = session;
  saving = (async () => {
    status('saving');
    if (s && !s.started) {
      s.started = true;
      s.lastSnapshotAt = Date.now();
      try {
        await snapshotBefore(s);
      } catch (err) {
        if (!isAuthError(err)) throw err;
      }
    }
    let ok = false;
    try {
      ok = await saveLevel(doc);
    } catch (err) {
      const now = useEditor.getState();
      if (isAuthError(err)) {
        if (now.doc === doc) now.set({ saveStatus: 'denied' });
        now.notify(err.status === 403 ? 'You no longer have access to save levels.' : 'Sign in again to save.');
        return false;
      }
      throw err;
    }
    const now = useEditor.getState();
    if (now.doc === doc) now.set({ dirty: false, savedAt: new Date().toISOString(), saveStatus: ok ? 'saved' : 'offline' });
    else {
      now.set({ saveStatus: 'pending' });
      schedule();
    }
    if (ok) {
      localStorage.setItem('ple:last', JSON.stringify({ id: doc.id, builtin: false }));
      if (s && s.id === doc.id) {
        s.savedDoc = doc;
        if (Date.now() - s.lastSnapshotAt >= SNAPSHOT_EVERY_MS) {
          s.lastSnapshotAt = Date.now();
          s.snapshotDoc = doc;
          void snapshot(doc);
        }
      }
    }
    return ok;
  })();
  try {
    return await saving;
  } finally {
    saving = null;
  }
}

/** Keep the current state as a named version (never thinned). */
export async function saveVersion(label: string): Promise<VersionMeta | null> {
  const ok = await saveNow();
  const doc = useEditor.getState().doc;
  if (!doc || !ok) return null;
  const v = await createVersion(doc, { thumb: renderThumbnail(doc), label, pinned: true });
  if (session?.id === doc.id) {
    session.snapshotDoc = doc;
    session.lastSnapshotAt = Date.now();
  }
  return v.version;
}

/** Replace the open level with a version (undoable; the current state is kept as a version first). */
export async function restoreVersion(v: VersionMeta) {
  const st = useEditor.getState();
  const cur = st.doc;
  if (!cur) return;
  const data = await loadVersion<LevelDoc>(v.levelId, v.id);
  await saveNow();
  await snapshot(cur);
  useEditor.getState().change('Restore version', () => withoutDerived({ ...data, id: cur.id }) as never);
  await saveNow();
}

/** Open a version as a separate new level. */
export async function openVersionAsCopy(v: VersionMeta) {
  const data = await loadVersion<LevelDoc>(v.levelId, v.id);
  await saveNow();
  const doc: LevelDoc = { ...data, id: uid('level'), name: `${data.name} (${new Date(v.createdAt).toLocaleDateString()})` };
  useEditor.getState().setDoc(doc, null);
  useEditor.getState().set({ modal: null, dirty: true });
  await saveNow();
}

function startSession(doc: LevelDoc, left: LevelDoc | null, leftDirty: boolean) {
  const prev = session;
  clearTimeout(timer);
  firstDirtyAt = 0;
  // the level being left: upload its last edits, and snapshot its final state if it changed since the last snapshot
  if (prev && left && left.id === prev.id) {
    const last = leftDirty ? left : prev.savedDoc;
    if (leftDirty && !isLobbyGuest(left.id)) void saveLevel(left);
    if (last && prev.started && last !== prev.snapshotDoc && !isLobbyGuest(left.id)) void snapshot(last);
  }
  session = { id: doc.id, opened: doc, started: false, lastSnapshotAt: 0, snapshotDoc: null, savedDoc: null };
  status(isLobbyGuest(doc.id) ? 'lobby' : 'saved');
}

/** Wire autosave to the editor store; returns the cleanup. */
export function startAutosave(): () => void {
  const unsub = useEditor.subscribe((s, p) => {
    // setDoc (a level was opened / created / imported): fresh history and not dirty
    if (s.doc && s.doc !== p.doc && !s.dirty && s.past.length === 0 && s.future.length === 0) {
      startSession(s.doc, p.doc, p.dirty);
      return;
    }
    if (s.dirty && s.doc && (s.doc !== p.doc || !p.dirty)) {
      if (isLobbyGuest(s.doc.id)) {
        saveLevelLocally(s.doc);
        status('lobby');
        return;
      }
      if (!p.dirty || firstDirtyAt === 0) firstDirtyAt = Date.now();
      if (s.saveStatus !== 'saving' && s.saveStatus !== 'pending') status('pending');
      schedule();
    }
    if (!s.dirty) firstDirtyAt = 0;
  });
  const flushLocal = () => {
    const s = useEditor.getState();
    return !s.doc || !s.dirty || saveLevelLocally(s.doc);
  };
  const beforeUnload = (e: BeforeUnloadEvent) => {
    if (!flushLocal()) e.preventDefault();
  };
  const onHide = () => {
    if (document.visibilityState !== 'hidden') return;
    flushLocal();
    if (useEditor.getState().dirty) void saveNow();
  };
  document.addEventListener('visibilitychange', onHide);
  window.addEventListener('pagehide', flushLocal);
  window.addEventListener('beforeunload', beforeUnload);
  const st = useEditor.getState();
  if (st.doc) startSession(st.doc, null, false);
  return () => {
    unsub();
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onHide);
    window.removeEventListener('pagehide', flushLocal);
    window.removeEventListener('beforeunload', beforeUnload);
  };
}
